import React from "react";
import { motion } from "framer-motion";
import { Section } from "./age-of-ai-components/Section";
import { ContentCard } from "./age-of-ai-components/ContentCard";
import { SectionHeader } from "./age-of-ai-components/SectionHeader";
import { TextReveal } from "./age-of-ai-components/TextReveal";
import { ParallaxText } from "./age-of-ai-components/ParallaxText";
import { TOCItem } from "./age-of-ai-components/TOCItem";
import { TechStackSection } from "./age-of-ai-components/TechStackSection";
import { UserTestingResultsSection } from "./age-of-ai-components/UserTestingResultsSection";
import "./index.css";
import { MockupsSection } from "./age-of-ai-components/MockupsSection";

function AgeOfAIPage() {
  return (
    <div className="bg-[#0d0d0d] min-h-screen text-white overflow-y-hidden">
      {/* Hero Section */}
      <Section className="min-h-[60vh] md:min-h-screen flex items-center justify-center relative">
        {/* Hero Section */}
        <div className="absolute inset-0 opacity-20">
          <img
            src="/assets/imgs/case-studies/age_of_ai_2.png"
            alt="Fresh vegetables"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0d0d0d] via-transparent to-[#0d0d0d]" />
        </div>

        <div className="relative z-10 text-center px-6 max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          >
            <motion.div
              className="inline-block mb-6 px-6 py-2 bg-[#fd5b38]/10 item-border border-[#fd5b38]/30 rounded-full"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
            >
              <span className="text-[#fd5b38]">UX Case Study 2025</span>
            </motion.div>

            <motion.h1
              className="mb-6"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.8 }}
            >
              <span className="block text-3xl md:text-5xl lg:text-6xl mb-4">
                The New Age of <span className="text-[#FD5B38]">AI</span> in
                Design
              </span>
              <span className="block text-2xl md:text-3xl lg:text-4xl text-gray-400">
                Al-Powered UX Optimization Tool
              </span>
            </motion.h1>

            <motion.p
              className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto mb-12"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.8 }}
            >
              Nithin Chandra Namani
            </motion.p>
          </motion.div>
        </div>

        <motion.div
          className="absolute bottom-10 left-1/2 -translate-x-1/2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 1 }}
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 2 }}
            className="text-[#fd5b38]"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
            >
              <path
                d="M12 5v14M19 12l-7 7-7-7"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </motion.div>
        </motion.div>
      </Section>

      {/* Table of Contents */}
      <Section id="toc" className="relative">
        <div className="absolute top-0 left-1/2 w-px h-64 bg-gradient-to-b from-transparent via-[#fd5b38] to-transparent opacity-20" />
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="mb-8 md:mb-12">
            <TextReveal>
              <h2 className="text-white mb-2 md:mb-4 text-[48px] md:text-[60px] leading-[48px] md:leading-[60px]">
                Table of Contents
              </h2>
            </TextReveal>
            <TextReveal delay={0.1}>
              <p className="text-[#6a7282] text-[20px] md:text-[24px] leading-[28px] md:leading-[32px]">
                (Design Index)
              </p>
            </TextReveal>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
            <div className="space-y-3 md:space-y-4">
              <TOCItem number="1." title="Abstract" delay={0} />
              <TOCItem
                number="3."
                title="Background and Context"
                delay={0.05}
              />
              <TOCItem
                number="5."
                title="Research Methodology and Design Framework"
                delay={0.1}
              />
              <TOCItem
                number="7."
                title="Target Audience and Use-Case Scenarios"
                delay={0.15}
              />
              <TOCItem
                number="9."
                title="Core Features and System Architecture"
                delay={0.2}
              />
              <TOCItem
                number="11."
                title="Prototyping, Testing, and Evaluation"
                delay={0.25}
              />
              <TOCItem
                number="13."
                title="Applied AI & ML Techniques"
                delay={0.3}
              />
              <TOCItem
                number="15."
                title="Benefits and Multidimensional Impact"
                delay={0.35}
              />
              <TOCItem number="17." title="Conclusion" delay={0.4} />
              <TOCItem number="19." title="Key Takeaways" delay={0.45} />
            </div>
            <div className="space-y-3 md:space-y-4">
              <TOCItem number="2." title="Introduction" delay={0.05} />
              <TOCItem number="4." title="Problem Statement" delay={0.1} />
              <TOCItem
                number="6."
                title="Key Findings and Competitive Landscape"
                delay={0.15}
              />
              <TOCItem
                number="8."
                title="Project Objectives and Success Metrics"
                delay={0.2}
              />
              <TOCItem
                number="10."
                title="UX Flow and Interaction Model"
                delay={0.25}
              />
              <TOCItem
                number="12."
                title="Technology Stack and Infrastructure"
                delay={0.3}
              />
              <TOCItem
                number="14."
                title="Discussion of Results and Insights"
                delay={0.35}
              />
              <TOCItem
                number="16."
                title="Risk & Ethical Considerations"
                delay={0.4}
              />
              <TOCItem number="18." title="Future Scope" delay={0.45} />
            </div>
          </div>
        </div>
      </Section>

      {/* Section 1: Abstract */}
      <Section id="abstract" className="relative">
        <div className="absolute top-0 right-0 w-64 h-64 bg-[#fd5b38] rounded-full blur-[150px] opacity-10 pointer-events-none" />
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <ContentCard>
            <SectionHeader number="1" title="Abstract" />
            <div className="mt-6 md:mt-8 space-y-4">
              <ParallaxText offset={20}>
                <TextReveal delay={0.2}>
                  <p className="text-[#d1d5dc]">
                    This case study presents the conception, development, and
                    evaluation of the AI-Powered UX Optimization Tool, an
                    intelligent platform designed to automate usability analysis
                    and deliver heuristic-driven recommendations.
                  </p>
                </TextReveal>
              </ParallaxText>
              <ParallaxText offset={15}>
                <TextReveal delay={0.3}>
                  <p className="text-[#d1d5dc]">
                    The system integrates machine learning (ML), natural
                    language processing (NLP), and human-centered design
                    methodologies to bridge the gap between raw user-testing
                    data and actionable design improvement.
                  </p>
                </TextReveal>
              </ParallaxText>
              <ParallaxText offset={10}>
                <TextReveal delay={0.4}>
                  <p className="text-[#d1d5dc]">
                    Through mixed-method research involving designer interviews,
                    workflow audits, and comparative analysis of existing tools,
                    the project identifies inefficiencies in manual UX processes
                    and proposes an AI-augmented solution that increases
                    accuracy, consistency, and accessibility.
                  </p>
                </TextReveal>
              </ParallaxText>
              <ParallaxText offset={5}>
                <TextReveal delay={0.5}>
                  <p className="text-[#d1d5dc]">
                    The outcome demonstrates a 43% reduction in analysis time,
                    improved alignment among stakeholders, and a reproducible
                    model for data-driven, ethical UX optimization.
                  </p>
                </TextReveal>
              </ParallaxText>
            </div>
          </ContentCard>
        </div>
      </Section>

      {/* Section 2: Introduction */}
      <Section id="introduction" className="relative">
        <div className="absolute top-0 right-0 w-64 h-64 bg-[#fd5b38] rounded-full blur-[150px] opacity-10 pointer-events-none" />
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <ContentCard>
            <SectionHeader number="2" title="Introduction" />
            <div className="mt-6 md:mt-8 space-y-4">
              <ParallaxText offset={20}>
                <TextReveal delay={0.2}>
                  <p className="text-[#d1d5dc]">
                    Design excellence today is measured not merely by aesthetic
                    appeal but by usability, accessibility, and adaptability.
                  </p>
                </TextReveal>
              </ParallaxText>
              <ParallaxText offset={15}>
                <TextReveal delay={0.3}>
                  <p className="text-[#d1d5dc]">
                    However, traditional UX analysis remains
                    labor-intensive—relying on human observation and subjective
                    interpretation of user data.
                  </p>
                </TextReveal>
              </ParallaxText>
              <ParallaxText offset={10}>
                <TextReveal delay={0.4}>
                  <p className="text-[#d1d5dc]">
                    In a typical design sprint, researchers spend days coding
                    transcripts, tagging friction points, and synthesizing
                    findings.
                  </p>
                </TextReveal>
              </ParallaxText>
              <ParallaxText offset={5}>
                <TextReveal delay={0.5}>
                  <p className="text-[#d1d5dc]">
                    This bottleneck delays iteration and inflates cost.
                  </p>
                </TextReveal>
              </ParallaxText>
              <ParallaxText offset={15}>
                <TextReveal delay={0.6}>
                  <p className="text-[#d1d5dc]">
                    The AI-Powered UX Optimization Tool (APUOT) was created to
                    address this inefficiency.
                  </p>
                </TextReveal>
              </ParallaxText>
              <ParallaxText offset={10}>
                <TextReveal delay={0.7}>
                  <p className="text-[#d1d5dc]">
                    It acts as an intelligent companion for UX teams, capable of
                    understanding natural-language queries, analyzing behavioral
                    data, and providing prescriptive, evidence-based design
                    suggestions aligned with Nielsen's Usability Heuristics and
                    WCAG 2.1 accessibility guidelines.
                  </p>
                </TextReveal>
              </ParallaxText>
              <ParallaxText offset={5}>
                <TextReveal delay={0.8}>
                  <p className="text-[#d1d5dc]">
                    By embedding AI into the discovery and evaluation phases of
                    the design lifecycle, APUOT transforms usability testing
                    from a reactive process into a continuous, adaptive, and
                    data-driven cycle.
                  </p>
                </TextReveal>
              </ParallaxText>
            </div>
          </ContentCard>
        </div>
      </Section>

      {/* Section 3: Background and Context */}
      <Section id="background" className="relative">
        <div className="absolute top-0 right-0 w-64 h-64 bg-[#fd5b38] rounded-full blur-[150px] opacity-10 pointer-events-none" />
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <ContentCard>
            <SectionHeader number="3" title="Background and Context" />
            <div className="mt-6 md:mt-8 space-y-6 md:space-y-8">
              {/* Subsection 3.1 */}
              <div className="border-l-2 border-[rgba(253,91,56,0.3)] pl-4 md:pl-6 space-y-3">
                <ParallaxText offset={15}>
                  <TextReveal delay={0.2}>
                    <h3 className="text-[#fd5b38] text-[20px] md:text-[24px] leading-[28px] md:leading-[32px]">
                      3.1 Industry Shift Toward AI in Design
                    </h3>
                  </TextReveal>
                </ParallaxText>
                <ParallaxText offset={12}>
                  <TextReveal delay={0.3}>
                    <p className="text-[#d1d5dc]">
                      Over the past decade, UX practice has matured from
                      intuitive artistry to a research-anchored discipline.
                    </p>
                  </TextReveal>
                </ParallaxText>
                <ParallaxText offset={10}>
                  <TextReveal delay={0.4}>
                    <p className="text-[#d1d5dc]">
                      Simultaneously, the rise of AI-as-a-Service (AIaaS) and
                      low-code platforms has made machine intelligence
                      accessible to non-technical domains.
                    </p>
                  </TextReveal>
                </ParallaxText>
                <ParallaxText offset={8}>
                  <TextReveal delay={0.5}>
                    <p className="text-[#d1d5dc]">
                      Designers now use AI to generate wireframes (Uizard), test
                      prototypes (Maze), or predict sentiment (MonkeyLearn).
                    </p>
                  </TextReveal>
                </ParallaxText>
                <ParallaxText offset={6}>
                  <TextReveal delay={0.6}>
                    <p className="text-[#d1d5dc]">
                      Yet most of these tools address isolated pain points.
                    </p>
                  </TextReveal>
                </ParallaxText>
                <ParallaxText offset={4}>
                  <TextReveal delay={0.7}>
                    <p className="text-[#d1d5dc]">
                      A cohesive framework that interprets user behavior, maps
                      it to heuristics, and closes the feedback loop is still
                      missing.
                    </p>
                  </TextReveal>
                </ParallaxText>
              </div>

              {/* Subsection 3.2 */}
              <div className="border-l-2 border-[rgba(253,91,56,0.3)] pl-4 md:pl-6 space-y-3">
                <ParallaxText offset={15}>
                  <TextReveal delay={0.2}>
                    <h3 className="text-[#fd5b38] text-[20px] md:text-[24px] leading-[28px] md:leading-[32px]">
                      3.2 The Human-Centered AI Approach
                    </h3>
                  </TextReveal>
                </ParallaxText>
                <ParallaxText offset={10}>
                  <TextReveal delay={0.3}>
                    <p className="text-[#d1d5dc]">
                      The project draws upon Human-Centered AI (HCAI) principles
                      (Shneiderman 2022), emphasizing collaboration between
                      humans and intelligent systems.
                    </p>
                  </TextReveal>
                </ParallaxText>
                <ParallaxText offset={5}>
                  <TextReveal delay={0.4}>
                    <p className="text-[#d1d5dc]">
                      Rather than replacing designers, the AI in APUOT acts as
                      an assistive analyst—augmenting human judgment with
                      pattern recognition, statistical rigor, and recall
                      capacity beyond human limits.
                    </p>
                  </TextReveal>
                </ParallaxText>
              </div>

              {/* Subsection 3.3 */}
              <div className="border-l-2 border-[rgba(253,91,56,0.3)] pl-4 md:pl-6 space-y-3">
                <ParallaxText offset={15}>
                  <TextReveal delay={0.2}>
                    <h3 className="text-[#fd5b38] text-[20px] md:text-[24px] leading-[28px] md:leading-[32px]">
                      3.3 Academic Foundations
                    </h3>
                  </TextReveal>
                </ParallaxText>
                <ParallaxText offset={10}>
                  <TextReveal delay={0.3}>
                    <p className="text-[#d1d5dc]">
                      The study references usability-testing literature (Nielsen
                      1994; Sauro & Lewis 2016) and AI design ethics frameworks
                      (IEEE Ethically Aligned Design 2021).
                    </p>
                  </TextReveal>
                </ParallaxText>
                <ParallaxText offset={5}>
                  <TextReveal delay={0.4}>
                    <p className="text-[#d1d5dc]">
                      These frameworks guided the tool's evaluation criteria:
                      effectiveness, efficiency, satisfaction, and transparency.
                    </p>
                  </TextReveal>
                </ParallaxText>
              </div>
            </div>
          </ContentCard>
        </div>
      </Section>

      {/* Section 4: Problem Statement */}
      <Section id="problem" className="relative">
        <div className="absolute top-0 right-0 w-64 h-64 bg-[#fd5b38] rounded-full blur-[150px] opacity-10 pointer-events-none" />
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <ContentCard>
            <SectionHeader number="4" title="Problem Statement" />
            <div className="mt-6 md:mt-8 space-y-4">
              <ParallaxText offset={20}>
                <TextReveal delay={0.2}>
                  <p className="text-[#d1d5dc]">
                    While digital products are proliferating, UX evaluation
                    remains bound by manual analysis cycles.
                  </p>
                </TextReveal>
              </ParallaxText>
              <ParallaxText offset={15}>
                <TextReveal delay={0.3}>
                  <p className="text-[#d1d5dc]">
                    Designers face recurring barriers:
                  </p>
                </TextReveal>
              </ParallaxText>
              <div className="space-y-3 ml-0 md:ml-4">
                <ParallaxText offset={12}>
                  <TextReveal delay={0.4}>
                    <div className="flex gap-3">
                      <span className="text-[#fd5b38] shrink-0">•</span>
                      <p className="text-[#d1d5dc]">
                        <strong>Manual Data Review</strong> - Hours of screen
                        recordings and transcripts must be watched and coded.
                      </p>
                    </div>
                  </TextReveal>
                </ParallaxText>
                <ParallaxText offset={10}>
                  <TextReveal delay={0.5}>
                    <div className="flex gap-3">
                      <span className="text-[#fd5b38] shrink-0">•</span>
                      <p className="text-[#d1d5dc]">
                        <strong>Inconsistent Interpretation</strong> - Findings
                        vary between evaluators, lowering reliability.
                      </p>
                    </div>
                  </TextReveal>
                </ParallaxText>
                <ParallaxText offset={8}>
                  <TextReveal delay={0.6}>
                    <div className="flex gap-3">
                      <span className="text-[#fd5b38] shrink-0">•</span>
                      <p className="text-[#d1d5dc]">
                        <strong>Fragmented Tool Ecosystem</strong> - Analytics,
                        prototyping, and reporting tools operate in silos.
                      </p>
                    </div>
                  </TextReveal>
                </ParallaxText>
                <ParallaxText offset={6}>
                  <TextReveal delay={0.7}>
                    <div className="flex gap-3">
                      <span className="text-[#fd5b38] shrink-0">•</span>
                      <p className="text-[#d1d5dc]">
                        <strong>Lack of Prescriptive Insight</strong> - Existing
                        software explains what went wrong but not why or how to
                        fix it.
                      </p>
                    </div>
                  </TextReveal>
                </ParallaxText>
                <ParallaxText offset={4}>
                  <TextReveal delay={0.8}>
                    <div className="flex gap-3">
                      <span className="text-[#fd5b38] shrink-0">•</span>
                      <p className="text-[#d1d5dc]">
                        <strong>Limited Accessibility Integration</strong> -
                        Accessibility testing remains reactive and isolated from
                        early design.
                      </p>
                    </div>
                  </TextReveal>
                </ParallaxText>
              </div>
              <ParallaxText offset={10}>
                <TextReveal delay={0.9}>
                  <p className="text-[#d1d5dc]">
                    These challenges motivated the creation of an integrated
                    AI-driven platform capable of end-to-end
                    analysis—automatically identifying, explaining, and
                    prioritizing usability issues.
                  </p>
                </TextReveal>
              </ParallaxText>
            </div>
          </ContentCard>
        </div>
      </Section>

      {/* Section 5: Research Methodology */}
      <Section id="methodology" className="relative">
        <div className="absolute top-0 right-0 w-64 h-64 bg-[#fd5b38] rounded-full blur-[150px] opacity-10 pointer-events-none" />
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <ContentCard>
            <SectionHeader
              number="5"
              title="Research Methodology and Design Framework"
            />
            <div className="mt-6 md:mt-8 space-y-4">
              <ParallaxText offset={20}>
                <TextReveal delay={0.2}>
                  <p className="text-[#d1d5dc]">
                    A mixed-method research design ensured both quantitative
                    rigor and qualitative depth.
                  </p>
                </TextReveal>
              </ParallaxText>
              <div className="space-y-3 ml-0 md:ml-4">
                <ParallaxText offset={15}>
                  <TextReveal delay={0.3}>
                    <div className="flex gap-3">
                      <span className="text-[#fd5b38] shrink-0">•</span>
                      <p className="text-[#d1d5dc]">
                        <strong>Exploratory Interviews:</strong> Five UX
                        designers + two product managers shared workflow pain
                        points.
                      </p>
                    </div>
                  </TextReveal>
                </ParallaxText>
                <ParallaxText offset={12}>
                  <TextReveal delay={0.4}>
                    <div className="flex gap-3">
                      <span className="text-[#fd5b38] shrink-0">•</span>
                      <p className="text-[#d1d5dc]">
                        <strong>Task Analysis:</strong> Mapped time allocation
                        in existing usability cycles.
                      </p>
                    </div>
                  </TextReveal>
                </ParallaxText>
                <ParallaxText offset={10}>
                  <TextReveal delay={0.5}>
                    <div className="flex gap-3">
                      <span className="text-[#fd5b38] shrink-0">•</span>
                      <p className="text-[#d1d5dc]">
                        <strong>Comparative Study:</strong> Benchmarked six
                        leading tools (Hotjar, Maze, FullStory, Uizard, Galileo
                        AI, Google Analytics).
                      </p>
                    </div>
                  </TextReveal>
                </ParallaxText>
                <ParallaxText offset={8}>
                  <TextReveal delay={0.6}>
                    <div className="flex gap-3">
                      <span className="text-[#fd5b38] shrink-0">•</span>
                      <p className="text-[#d1d5dc]">
                        <strong>Data Synthesis:</strong> Applied affinity
                        mapping to categorize themes: efficiency, insight
                        generation, and collaboration.
                      </p>
                    </div>
                  </TextReveal>
                </ParallaxText>
              </div>
              <div className="mt-6 space-y-3">
                <ParallaxText offset={15}>
                  <TextReveal delay={0.7}>
                    <h3 className="text-[#d1d5dc]">Analytical Framework</h3>
                  </TextReveal>
                </ParallaxText>
                <ParallaxText offset={10}>
                  <TextReveal delay={0.8}>
                    <p className="text-[#d1d5dc]">
                      The project followed the Double Diamond Model
                      (Discover-Define-Develop-Deliver), enhanced with AI
                      feedback loops.
                    </p>
                  </TextReveal>
                </ParallaxText>
                <ParallaxText offset={5}>
                  <TextReveal delay={0.9}>
                    <p className="text-[#d1d5dc]">
                      This approach ensured alignment between user needs and
                      technical feasibility.
                    </p>
                  </TextReveal>
                </ParallaxText>
              </div>
            </div>
          </ContentCard>
        </div>
      </Section>

      {/* Section 6: Key Findings and Competitive Landscape */}
      <Section id="findings" className="relative">
        <div className="absolute top-0 right-0 w-64 h-64 bg-[#fd5b38] rounded-full blur-[150px] opacity-10 pointer-events-none" />
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <ContentCard>
            <SectionHeader
              number="6"
              title="Key Findings and Competitive Landscape"
            />
            <div className="mt-6 md:mt-8 space-y-4">
              <ParallaxText offset={15}>
                <TextReveal delay={0.2}>
                  <p className="text-[#d1d5dc]">
                    <strong>Insight Extraction Delay:</strong> Teams required
                    2-3 days post-testing to compile reports.
                  </p>
                </TextReveal>
              </ParallaxText>
              <ParallaxText offset={12}>
                <TextReveal delay={0.3}>
                  <p className="text-[#d1d5dc]">
                    <strong>Tool Fatigue:</strong> Average of five different
                    applications per project.
                  </p>
                </TextReveal>
              </ParallaxText>
              <ParallaxText offset={10}>
                <TextReveal delay={0.4}>
                  <p className="text-[#d1d5dc]">
                    <strong>Low AI Utilization:</strong> AI used only for
                    sentiment tagging, not for design decision-making.
                  </p>
                </TextReveal>
              </ParallaxText>

              {/* Competitive Analysis Table */}
              <div className="mt-6 overflow-x-auto">
                <TextReveal delay={0.5}>
                  <div className="min-w-full">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="border-b-2 border-[#fd5b38]">
                          <th className="text-left p-3 md:p-4 text-[#fd5b38]">
                            Tool
                          </th>
                          <th className="text-left p-3 md:p-4 text-[#fd5b38]">
                            Strength
                          </th>
                          <th className="text-left p-3 md:p-4 text-[#fd5b38]">
                            Gap
                          </th>
                        </tr>
                      </thead>
                      <tbody className="text-[#d1d5dc] text-sm md:text-base">
                        <tr className="border-b border-[#1e2939]">
                          <td className="p-3 md:p-4">Hotjar</td>
                          <td className="p-3 md:p-4">
                            Heatmaps, session recording
                          </td>
                          <td className="p-3 md:p-4">
                            No diagnostic explanation
                          </td>
                        </tr>
                        <tr className="border-b border-[#1e2939]">
                          <td className="p-3 md:p-4">Maze</td>
                          <td className="p-3 md:p-4">Prototype integration</td>
                          <td className="p-3 md:p-4">
                            Lacks deep semantic analytics
                          </td>
                        </tr>
                        <tr className="border-b border-[#1e2939]">
                          <td className="p-3 md:p-4">Uizard</td>
                          <td className="p-3 md:p-4">Generative design</td>
                          <td className="p-3 md:p-4">
                            Not optimization-focused
                          </td>
                        </tr>
                        <tr className="border-b border-[#1e2939]">
                          <td className="p-3 md:p-4">FullStory</td>
                          <td className="p-3 md:p-4">Behavior replay</td>
                          <td className="p-3 md:p-4">Manual tagging</td>
                        </tr>
                        <tr className="border-b border-[#1e2939]">
                          <td className="p-3 md:p-4">Galileo AI</td>
                          <td className="p-3 md:p-4">Concept creation</td>
                          <td className="p-3 md:p-4">No evaluation loop</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </TextReveal>
              </div>

              <ParallaxText offset={8}>
                <TextReveal delay={0.6}>
                  <p className="text-[#d1d5dc] mt-6">
                    <strong>Gap:</strong> A unified, heuristic-aware AI system
                    capable of contextual insight generation and ethical
                    transparency.
                  </p>
                </TextReveal>
              </ParallaxText>
            </div>
          </ContentCard>
        </div>
      </Section>

      {/* Section 7: Target Audience */}
      <Section id="audience" className="relative">
        <div className="absolute top-0 right-0 w-64 h-64 bg-[#fd5b38] rounded-full blur-[150px] opacity-10 pointer-events-none" />
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <ContentCard>
            <SectionHeader
              number="7"
              title="Target Audience and Use-Case Scenarios"
            />
            <div className="mt-6 md:mt-8 space-y-6 md:space-y-8">
              {/* Primary Users */}
              <div className="border-l-2 border-[rgba(253,91,56,0.3)] pl-4 md:pl-6 space-y-3">
                <ParallaxText offset={15}>
                  <TextReveal delay={0.2}>
                    <h3 className="text-[#fd5b38] text-xl md:text-2xl">
                      7.1 Primary Users
                    </h3>
                  </TextReveal>
                </ParallaxText>
                <div className="space-y-2">
                  <ParallaxText offset={12}>
                    <TextReveal delay={0.3}>
                      <div className="flex gap-3">
                        <span className="text-[#fd5b38] shrink-0">•</span>
                        <p className="text-[#d1d5dc]">
                          <strong>UX Designers:</strong> Rapid validation of
                          design hypotheses.
                        </p>
                      </div>
                    </TextReveal>
                  </ParallaxText>
                  <ParallaxText offset={10}>
                    <TextReveal delay={0.4}>
                      <div className="flex gap-3">
                        <span className="text-[#fd5b38] shrink-0">•</span>
                        <p className="text-[#d1d5dc]">
                          <strong>Product Managers:</strong> Evidence-based
                          decision support.
                        </p>
                      </div>
                    </TextReveal>
                  </ParallaxText>
                  <ParallaxText offset={8}>
                    <TextReveal delay={0.5}>
                      <div className="flex gap-3">
                        <span className="text-[#fd5b38] shrink-0">•</span>
                        <p className="text-[#d1d5dc]">
                          <strong>Usability Researchers:</strong> Automated
                          insight extraction.
                        </p>
                      </div>
                    </TextReveal>
                  </ParallaxText>
                  <ParallaxText offset={6}>
                    <TextReveal delay={0.6}>
                      <div className="flex gap-3">
                        <span className="text-[#fd5b38] shrink-0">•</span>
                        <p className="text-[#d1d5dc]">
                          <strong>Small Agencies / Freelancers:</strong>{" "}
                          Enterprise-grade analytics without infrastructure
                          overhead.
                        </p>
                      </div>
                    </TextReveal>
                  </ParallaxText>
                </div>
              </div>

              {/* Use Cases */}
              <div className="border-l-2 border-[rgba(253,91,56,0.3)] pl-4 md:pl-6 space-y-3">
                <ParallaxText offset={15}>
                  <TextReveal delay={0.2}>
                    <h3 className="text-[#fd5b38] text-xl md:text-2xl">
                      7.2 Representative Use Cases
                    </h3>
                  </TextReveal>
                </ParallaxText>
                <div className="space-y-2">
                  <ParallaxText offset={12}>
                    <TextReveal delay={0.3}>
                      <div className="flex gap-3">
                        <span className="text-[#fd5b38] shrink-0">•</span>
                        <p className="text-[#d1d5dc]">
                          <strong>A/B Testing Summarization</strong> - AI
                          identifies statistically significant differences.
                        </p>
                      </div>
                    </TextReveal>
                  </ParallaxText>
                  <ParallaxText offset={10}>
                    <TextReveal delay={0.4}>
                      <div className="flex gap-3">
                        <span className="text-[#fd5b38] shrink-0">•</span>
                        <p className="text-[#d1d5dc]">
                          <strong>Friction Detection in User Flows</strong> -
                          Heat-map visualization and anomaly detection.
                        </p>
                      </div>
                    </TextReveal>
                  </ParallaxText>
                  <ParallaxText offset={8}>
                    <TextReveal delay={0.5}>
                      <div className="flex gap-3">
                        <span className="text-[#fd5b38] shrink-0">•</span>
                        <p className="text-[#d1d5dc]">
                          <strong>Accessibility Audits</strong> - Auto-flagging
                          WCAG violations.
                        </p>
                      </div>
                    </TextReveal>
                  </ParallaxText>
                  <ParallaxText offset={6}>
                    <TextReveal delay={0.6}>
                      <div className="flex gap-3">
                        <span className="text-[#fd5b38] shrink-0">•</span>
                        <p className="text-[#d1d5dc]">
                          <strong>Insight Collaboration</strong> - Shareable AI
                          reports with editable confidence notes.
                        </p>
                      </div>
                    </TextReveal>
                  </ParallaxText>
                </div>
              </div>
            </div>
          </ContentCard>
        </div>
      </Section>

      {/* Section 8: Project Objectives */}
      <Section id="objectives" className="relative">
        <div className="absolute top-0 right-0 w-64 h-64 bg-[#fd5b38] rounded-full blur-[150px] opacity-10 pointer-events-none" />
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <ContentCard>
            <SectionHeader
              number="8"
              title="Project Objectives and Success Metrics"
            />
            <div className="mt-6 md:mt-8">
              <div className="overflow-x-auto">
                <TextReveal delay={0.2}>
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b-2 border-[#fd5b38]">
                        <th className="text-left p-3 md:p-4 text-[#fd5b38]">
                          Objective
                        </th>
                        <th className="text-left p-3 md:p-4 text-[#fd5b38]">
                          KPI/Target Outcome
                        </th>
                      </tr>
                    </thead>
                    <tbody className="text-[#d1d5dc] text-sm md:text-base">
                      <tr className="border-b border-[#1e2939]">
                        <td className="p-3 md:p-4">Reduce time-to-insight</td>
                        <td className="p-3 md:p-4">
                          40% reduction in analysis time
                        </td>
                      </tr>
                      <tr className="border-b border-[#1e2939]">
                        <td className="p-3 md:p-4">
                          Increase finding accuracy
                        </td>
                        <td className="p-3 md:p-4">
                          90% precision in issue identification
                        </td>
                      </tr>
                      <tr className="border-b border-[#1e2939]">
                        <td className="p-3 md:p-4">
                          Ensure accessibility compliance
                        </td>
                        <td className="p-3 md:p-4">
                          100% WCAG 2.1 AA coverage
                        </td>
                      </tr>
                      <tr className="border-b border-[#1e2939]">
                        <td className="p-3 md:p-4">
                          Enable cross-team collaboration
                        </td>
                        <td className="p-3 md:p-4">
                          Multi-stakeholder report sharing
                        </td>
                      </tr>
                      <tr className="border-b border-[#1e2939]">
                        <td className="p-3 md:p-4">
                          Maintain ethical transparency
                        </td>
                        <td className="p-3 md:p-4">
                          Explainable AI outputs with confidence scores
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </TextReveal>
              </div>
            </div>
          </ContentCard>
        </div>
      </Section>

      {/* Section 9: Core Features and System Architecture */}
      <Section id="core-features" className="relative">
        <div className="absolute top-0 right-0 w-64 h-64 bg-[#fd5b38] rounded-full blur-[150px] opacity-10 pointer-events-none" />
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <ContentCard>
            <SectionHeader
              number="9"
              title="Core Features and System Architecture"
            />
            <div className="mt-6 md:mt-8 space-y-6">
              <ParallaxText offset={20}>
                <TextReveal delay={0.2}>
                  <h3 className="text-[#d1d5dc]">Functional Modules</h3>
                </TextReveal>
              </ParallaxText>

              <div className="space-y-3 ml-0 md:ml-4">
                <ParallaxText offset={15}>
                  <TextReveal delay={0.3}>
                    <div className="flex gap-3">
                      <span className="text-[#fd5b38] shrink-0">•</span>
                      <p className="text-[#d1d5dc]">
                        <strong>Smart Search & NLP Interface</strong> -
                        Conversational AI for querying usability insights.
                      </p>
                    </div>
                  </TextReveal>
                </ParallaxText>
                <ParallaxText offset={13}>
                  <TextReveal delay={0.4}>
                    <div className="flex gap-3">
                      <span className="text-[#fd5b38] shrink-0">•</span>
                      <p className="text-[#d1d5dc]">
                        <strong>Voice Input</strong> - Enables hands-free
                        annotation during test playback.
                      </p>
                    </div>
                  </TextReveal>
                </ParallaxText>
                <ParallaxText offset={11}>
                  <TextReveal delay={0.5}>
                    <div className="flex gap-3">
                      <span className="text-[#fd5b38] shrink-0">•</span>
                      <p className="text-[#d1d5dc]">
                        <strong>Behaviour Analytics Engine</strong> - Tracks
                        click-density, scroll depth, and time-on-task.
                      </p>
                    </div>
                  </TextReveal>
                </ParallaxText>
                <ParallaxText offset={9}>
                  <TextReveal delay={0.6}>
                    <div className="flex gap-3">
                      <span className="text-[#fd5b38] shrink-0">•</span>
                      <p className="text-[#d1d5dc]">
                        <strong>Friction Mapping Dashboard</strong> - Visual
                        layer using color gradients for confusion zones.
                      </p>
                    </div>
                  </TextReveal>
                </ParallaxText>
                <ParallaxText offset={7}>
                  <TextReveal delay={0.7}>
                    <div className="flex gap-3">
                      <span className="text-[#fd5b38] shrink-0">•</span>
                      <p className="text-[#d1d5dc]">
                        <strong>AI Recommendation Panel</strong> - Lists ranked
                        issues with heuristic tags.
                      </p>
                    </div>
                  </TextReveal>
                </ParallaxText>
                <ParallaxText offset={5}>
                  <TextReveal delay={0.8}>
                    <div className="flex gap-3">
                      <span className="text-[#fd5b38] shrink-0">•</span>
                      <p className="text-[#d1d5dc]">
                        <strong>Comparison Mode</strong> - "Before vs After"
                        screens with measurable improvement scores.
                      </p>
                    </div>
                  </TextReveal>
                </ParallaxText>
                <ParallaxText offset={3}>
                  <TextReveal delay={0.9}>
                    <div className="flex gap-3">
                      <span className="text-[#fd5b38] shrink-0">•</span>
                      <p className="text-[#d1d5dc]">
                        <strong>Reporting Suite</strong> - Exports PDF/CSV with
                        charts and AI summary text.
                      </p>
                    </div>
                  </TextReveal>
                </ParallaxText>
              </div>

              <div className="mt-8 space-y-3">
                <ParallaxText offset={15}>
                  <TextReveal delay={0.3}>
                    <h3 className="text-[#d1d5dc]">System Architecture</h3>
                  </TextReveal>
                </ParallaxText>
                <ParallaxText offset={10}>
                  <TextReveal delay={0.4}>
                    <p className="text-[#d1d5dc]">
                      Front-end in React + Figma UI kit, middleware using Flask
                      API, backend on AWS Lambda, and AI models served via
                      OpenAI and TensorFlow endpoints.
                    </p>
                  </TextReveal>
                </ParallaxText>
              </div>
            </div>
          </ContentCard>
        </div>
      </Section>

      <Section id="flow-abd-interaction-models" className="relative">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <SectionHeader
            number="10"
            title="UX Flow and Interaction Model"
            subtitle="User Journey Visualization"
          />
          <TextReveal delay={0.2}>
            <div className="flex items-center gap-2 text-[#6a7282] mt-12">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 20 20">
                <path
                  d="M9.1812 2.34537C9.21691 2.1542 9.31835 1.98155 9.46795 1.8573C9.61755 1.73305 9.8059 1.66504 10.0004 1.66504C10.1948 1.66504 10.3832 1.73305 10.5328 1.8573C10.6824 1.98155 10.7838 2.1542 10.8195 2.34537L11.6954 6.97703C11.7576 7.30632 11.9176 7.60922 12.1546 7.84618C12.3915 8.08314 12.6944 8.24316 13.0237 8.30537L17.6554 9.1812C17.8465 9.21691 18.0192 9.31835 18.1434 9.46795C18.2677 9.61755 18.3357 9.8059 18.3357 10.0004C18.3357 10.1948 18.2677 10.3832 18.1434 10.5328C18.0192 10.6824 17.8465 10.7838 17.6554 10.8195L13.0237 11.6954C12.6944 11.7576 12.3915 11.9176 12.1546 12.1546C11.9176 12.3915 11.7576 12.6944 11.6954 13.0237L10.8195 17.6554C10.7838 17.8465 10.6824 18.0192 10.5328 18.1434C10.3832 18.2677 10.1948 18.3357 10.0004 18.3357C9.8059 18.3357 9.61755 18.2677 9.46795 18.1434C9.31835 18.0192 9.21691 17.8465 9.1812 17.6554L8.30537 13.0237C8.24316 12.6944 8.08314 12.3915 7.84618 12.1546C7.60922 11.9176 7.30632 11.7576 6.97703 11.6954L2.34537 10.8195C2.1542 10.7838 1.98155 10.6824 1.8573 10.5328C1.73305 10.3832 1.66504 10.1948 1.66504 10.0004C1.66504 9.8059 1.73305 9.61755 1.8573 9.46795C1.98155 9.31835 2.1542 9.21691 2.34537 9.1812L6.97703 8.30537C7.30632 8.24316 7.60922 8.08314 7.84618 7.84618C8.08314 7.60922 8.24316 7.30632 8.30537 6.97703L9.1812 2.34537Z"
                  stroke="#FD5B38"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="1.66667"
                />
                <path
                  d="M16.6665 1.66699V5.00033"
                  stroke="#FD5B38"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="1.66667"
                />
                <path
                  d="M18.3333 3.33301H15"
                  stroke="#FD5B38"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="1.66667"
                />
                <path
                  d="M3.33333 18.3333C4.25381 18.3333 5 17.5871 5 16.6667C5 15.7462 4.25381 15 3.33333 15C2.41286 15 1.66667 15.7462 1.66667 16.6667C1.66667 17.5871 2.41286 18.3333 3.33333 18.3333Z"
                  stroke="#FD5B38"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="1.66667"
                />
              </svg>
              <span className="text-sm md:text-base">
                User Journey Visualization
              </span>
            </div>
          </TextReveal>
          <div>
            <img
              src="/assets/imgs/case-studies/age_of_ai_1.png"
              alt="UX Flow Diagram"
              className="w-full mt-8 rounded-lg shadow-lg"
            />
          </div>

          <UserTestingResultsSection />
        </div>
      </Section>

      <Section id="mockuo-section" className="relative">
        <MockupsSection />
      </Section>

      {/* Section 11: Prototyping, Testing, and Evaluation*/}
      <Section id="background" className="relative">
        <div className="absolute top-0 right-0 w-64 h-64 bg-[#fd5b38] rounded-full blur-[150px] opacity-10 pointer-events-none" />
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <ContentCard>
            <SectionHeader
              number="11"
              title="Prototyping, Testing, and Evaluation"
            />
            <div className="mt-6 md:mt-8 space-y-6 md:space-y-8">
              {/* Subsection 11.1 */}
              <div className="border-l-2 border-[rgba(253,91,56,0.3)] pl-4 md:pl-6 space-y-3">
                <ParallaxText offset={15}>
                  <TextReveal delay={0.2}>
                    <h3 className="text-[#fd5b38] text-[20px] md:text-[24px] leading-[28px] md:leading-[32px]">
                      11.1 Testing Method
                    </h3>
                  </TextReveal>
                </ParallaxText>
                <ParallaxText offset={12}>
                  <TextReveal delay={0.3}>
                    <p className="text-[#d1d5dc]">
                      Three moderate remote sessions + surveys.
                    </p>
                  </TextReveal>
                </ParallaxText>
                <ParallaxText offset={10}>
                  <TextReveal delay={0.4}>
                    <p className="text-[#d1d5dc]">
                      Participants: 2 senior designers, 1 research lead.
                    </p>
                  </TextReveal>
                </ParallaxText>
                <ParallaxText offset={8}>
                  <TextReveal delay={0.5}>
                    <p className="text-[#d1d5dc]">
                      Metrics: task completion time, error count, SUS score.
                    </p>
                  </TextReveal>
                </ParallaxText>
              </div>

              {/* Subsection 11.2 */}
              <div className="border-l-2 border-[rgba(253,91,56,0.3)] pl-4 md:pl-6 space-y-3">
                <ParallaxText offset={15}>
                  <TextReveal delay={0.2}>
                    <h3 className="text-[#fd5b38] text-[20px] md:text-[24px] leading-[28px] md:leading-[32px]">
                      11.2 Results
                    </h3>
                  </TextReveal>
                </ParallaxText>
                <ParallaxText offset={10}>
                  <TextReveal delay={0.3}>
                    <p className="text-[#d1d5dc]">
                      Analysis Time ↓ 43% (from 90→51 minutes).
                    </p>
                  </TextReveal>
                </ParallaxText>
                <ParallaxText offset={5}>
                  <TextReveal delay={0.4}>
                    <p className="text-[#d1d5dc]">
                      User Satisfaction $= 8.6/10$.
                    </p>
                  </TextReveal>
                </ParallaxText>
                <ParallaxText offset={5}>
                  <TextReveal delay={0.4}>
                    <p className="text-[#d1d5dc]">Report Clarity ↑ 70%.</p>
                  </TextReveal>
                </ParallaxText>
              </div>

              {/* Subsection 11.3 */}
              <div className="border-l-2 border-[rgba(253,91,56,0.3)] pl-4 md:pl-6 space-y-3">
                <ParallaxText offset={15}>
                  <TextReveal delay={0.2}>
                    <h3 className="text-[#fd5b38] text-[20px] md:text-[24px] leading-[28px] md:leading-[32px]">
                      11.3 Qualitative Feedback
                    </h3>
                  </TextReveal>
                </ParallaxText>
                <ParallaxText offset={10}>
                  <TextReveal delay={0.3}>
                    <p className="text-[#d1d5dc]">
                      "It feels like having a junior researcher built into the
                      tool."
                    </p>
                  </TextReveal>
                </ParallaxText>
                <ParallaxText offset={5}>
                  <TextReveal delay={0.4}>
                    <p className="text-[#d1d5dc]">
                      "The Al summaries save so much post-session effort."
                    </p>
                  </TextReveal>
                </ParallaxText>
                <ParallaxText offset={5}>
                  <TextReveal delay={0.4}>
                    <p className="text-[#d1d5dc]">
                      Insights led to feature refinements: contextual tooltips,
                      improved export layout, and adaptive color legends for
                      heatmaps.
                    </p>
                  </TextReveal>
                </ParallaxText>
              </div>
            </div>
          </ContentCard>
        </div>
      </Section>

      {/* Section 12: */}

      <TechStackSection />

      {/* Section 13: Applied AI & ML Techniques*/}
      <Section id="problem" className="relative">
        <div className="absolute top-0 right-0 w-64 h-64 bg-[#fd5b38] rounded-full blur-[150px] opacity-10 pointer-events-none" />
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <ContentCard>
            <SectionHeader number="13" title="Applied AI & ML Techniques" />
            <div className="mt-6 md:mt-8 space-y-4">
              <div className="space-y-3 ml-0 md:ml-4">
                <ParallaxText offset={12}>
                  <TextReveal delay={0.4}>
                    <div className="flex gap-3">
                      <span className="text-[#fd5b38] shrink-0">•</span>
                      <p className="text-[#d1d5dc]">
                        Natural Language Processing (NLP) - Text summarization
                        of user feedback. - Sentiment and intent classification.
                      </p>
                    </div>
                  </TextReveal>
                </ParallaxText>
                <ParallaxText offset={10}>
                  <TextReveal delay={0.5}>
                    <div className="flex gap-3">
                      <span className="text-[#fd5b38] shrink-0">•</span>
                      <p className="text-[#d1d5dc]">
                        Supervised Learning (Regression / Classification) -
                        Predicts task success likelihood. - Maps user behavior
                        to friction scores.
                      </p>
                    </div>
                  </TextReveal>
                </ParallaxText>
                <ParallaxText offset={8}>
                  <TextReveal delay={0.6}>
                    <div className="flex gap-3">
                      <span className="text-[#fd5b38] shrink-0">•</span>
                      <p className="text-[#d1d5dc]">
                        Unsupervised Clustering (K-Means) - Groups similar error
                        patterns across projects.
                      </p>
                    </div>
                  </TextReveal>
                </ParallaxText>
                <ParallaxText offset={6}>
                  <TextReveal delay={0.7}>
                    <div className="flex gap-3">
                      <span className="text-[#fd5b38] shrink-0">•</span>
                      <p className="text-[#d1d5dc]">
                        Heuristic Mapping Algorithm - Aligns findings to
                        Nielsen's principles (e.g., visibility of system status,
                        error prevention).
                      </p>
                    </div>
                  </TextReveal>
                </ParallaxText>
                <ParallaxText offset={4}>
                  <TextReveal delay={0.8}>
                    <div className="flex gap-3">
                      <span className="text-[#fd5b38] shrink-0">•</span>
                      <p className="text-[#d1d5dc]">
                        Explainable AI (XAI) - Generates human-readable
                        reasoning for each recommendation.
                      </p>
                    </div>
                  </TextReveal>
                </ParallaxText>
              </div>
            </div>
          </ContentCard>
        </div>
      </Section>

      {/* Section 14: Discussion of Results and Insights */}
      <Section id="abstract" className="relative">
        <div className="absolute top-0 right-0 w-64 h-64 bg-[#fd5b38] rounded-full blur-[150px] opacity-10 pointer-events-none" />
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <ContentCard>
            <SectionHeader
              number="14"
              title="Discussion of Results and Insights"
            />
            <div className="mt-6 md:mt-8 space-y-4">
              <ParallaxText offset={20}>
                <TextReveal delay={0.2}>
                  <p className="text-[#d1d5dc]">
                    The integration of Al into UX analysis yielded both
                    quantitative and qualitative benefits:
                  </p>
                </TextReveal>
              </ParallaxText>
              <ParallaxText offset={15}>
                <TextReveal delay={0.3}>
                  <p className="text-[#d1d5dc]">
                    Efficiency Gain: Average analysis time cut nearly in half.
                  </p>
                </TextReveal>
              </ParallaxText>
              <ParallaxText offset={10}>
                <TextReveal delay={0.4}>
                  <p className="text-[#d1d5dc]">
                    Consistency: Inter-rater reliability increased to $0.84$
                    (Cohen's $\kappa$).
                  </p>
                </TextReveal>
              </ParallaxText>
              <ParallaxText offset={5}>
                <TextReveal delay={0.5}>
                  <p className="text-[#d1d5dc]">
                    Decision Clarity: Design teams reported fewer disputes over
                    prioritization.
                  </p>
                </TextReveal>
              </ParallaxText>
              <ParallaxText offset={5}>
                <TextReveal delay={0.5}>
                  <p className="text-[#d1d5dc]">
                    Inclusivity: Automatic WCAG checks identified accessibility
                    issues earlier in the cycle.
                  </p>
                </TextReveal>
              </ParallaxText>
              <ParallaxText offset={5}>
                <TextReveal delay={0.5}>
                  <p className="text-[#d1d5dc]">
                    The Al's explainable layer builds trust by revealing why
                    specific recommendations were made.
                  </p>
                </TextReveal>
              </ParallaxText>
            </div>
          </ContentCard>
        </div>
      </Section>

      {/* Section 15: Benefits and Multidimensional Impact*/}
      <Section id="background" className="relative">
        <div className="absolute top-0 right-0 w-64 h-64 bg-[#fd5b38] rounded-full blur-[150px] opacity-10 pointer-events-none" />
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <ContentCard>
            <SectionHeader
              number="15"
              title="Benefits and Multidimensional Impact"
            />
            <div className="mt-6 md:mt-8 space-y-6 md:space-y-8">
              {/* Subsection 15.1 */}
              <div className="border-l-2 border-[rgba(253,91,56,0.3)] pl-4 md:pl-6 space-y-3">
                <ParallaxText offset={15}>
                  <TextReveal delay={0.2}>
                    <h3 className="text-[#fd5b38] text-[20px] md:text-[24px] leading-[28px] md:leading-[32px]">
                      Social Impact
                    </h3>
                  </TextReveal>
                </ParallaxText>
                <ParallaxText offset={12}>
                  <TextReveal delay={0.3}>
                    <div className="flex gap-3">
                      <span className="text-[#fd5b38] shrink-0">•</span>
                      <p className="text-[#d1d5dc]">
                        Democratizes research capacity for small teams.
                      </p>
                    </div>
                  </TextReveal>
                </ParallaxText>
                <ParallaxText offset={12}>
                  <TextReveal delay={0.3}>
                    <div className="flex gap-3">
                      <span className="text-[#fd5b38] shrink-0">•</span>
                      <p className="text-[#d1d5dc]">
                        Promotes inclusive design culture.
                      </p>
                    </div>
                  </TextReveal>
                </ParallaxText>
                <ParallaxText offset={12}>
                  <TextReveal delay={0.3}>
                    <div className="flex gap-3">
                      <span className="text-[#fd5b38] shrink-0">•</span>
                      <p className="text-[#d1d5dc]">
                        Enhances designer confidence through objective feedback.
                      </p>
                    </div>
                  </TextReveal>
                </ParallaxText>
              </div>

              {/* Subsection 15.2 */}
              <div className="border-l-2 border-[rgba(253,91,56,0.3)] pl-4 md:pl-6 space-y-3">
                <ParallaxText offset={15}>
                  <TextReveal delay={0.2}>
                    <h3 className="text-[#fd5b38] text-[20px] md:text-[24px] leading-[28px] md:leading-[32px]">
                      Economic Impact
                    </h3>
                  </TextReveal>
                </ParallaxText>
                <ParallaxText offset={12}>
                  <TextReveal delay={0.3}>
                    <div className="flex gap-3">
                      <span className="text-[#fd5b38] shrink-0">•</span>
                      <p className="text-[#d1d5dc]">
                        {`Reduces labor cost per test cycle by > 40%.`}
                      </p>
                    </div>
                  </TextReveal>
                </ParallaxText>
                <ParallaxText offset={12}>
                  <TextReveal delay={0.3}>
                    <div className="flex gap-3">
                      <span className="text-[#fd5b38] shrink-0">•</span>
                      <p className="text-[#d1d5dc]">
                        Accelerates time-to-market by 1-2 sprints.
                      </p>
                    </div>
                  </TextReveal>
                </ParallaxText>
                <ParallaxText offset={12}>
                  <TextReveal delay={0.3}>
                    <div className="flex gap-3">
                      <span className="text-[#fd5b38] shrink-0">•</span>
                      <p className="text-[#d1d5dc]">
                        Increases return on design investment through reusable
                        insight datasets.
                      </p>
                    </div>
                  </TextReveal>
                </ParallaxText>
              </div>

              {/* Subsection 15.3 */}
              <div className="border-l-2 border-[rgba(253,91,56,0.3)] pl-4 md:pl-6 space-y-3">
                <ParallaxText offset={15}>
                  <TextReveal delay={0.2}>
                    <h3 className="text-[#fd5b38] text-[20px] md:text-[24px] leading-[28px] md:leading-[32px]">
                      Environmental Impact
                    </h3>
                  </TextReveal>
                </ParallaxText>
                <ParallaxText offset={12}>
                  <TextReveal delay={0.3}>
                    <div className="flex gap-3">
                      <span className="text-[#fd5b38] shrink-0">•</span>
                      <p className="text-[#d1d5dc]">
                        Fewer redundant iterations $\rightarrow$ lower energy
                        use.
                      </p>
                    </div>
                  </TextReveal>
                </ParallaxText>
                <ParallaxText offset={12}>
                  <TextReveal delay={0.3}>
                    <div className="flex gap-3">
                      <span className="text-[#fd5b38] shrink-0">•</span>
                      <p className="text-[#d1d5dc]">
                        Cloud efficiency via on-demand compute reduces carbon
                        footprint.
                      </p>
                    </div>
                  </TextReveal>
                </ParallaxText>
              </div>
            </div>
          </ContentCard>
        </div>
      </Section>

      {/* Section 16: Risk & Ethical Considerations */}
      <Section id="problem" className="relative">
        <div className="absolute top-0 right-0 w-64 h-64 bg-[#fd5b38] rounded-full blur-[150px] opacity-10 pointer-events-none" />
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <ContentCard>
            <SectionHeader number="16" title="Risk & Ethical Considerations" />
            <div className="mt-6 md:mt-8 space-y-4">
              <ParallaxText offset={20}>
                <TextReveal delay={0.2}>
                  <p className="text-[#d1d5dc]">Risk Mitigation Strategy</p>
                </TextReveal>
              </ParallaxText>
              <div className="space-y-3 ml-0 md:ml-4">
                <ParallaxText offset={12}>
                  <TextReveal delay={0.4}>
                    <div className="flex gap-3">
                      <span className="text-[#fd5b38] shrink-0">•</span>
                      <p className="text-[#d1d5dc]">
                        Data Privacy: Encryption (AES-256), GDPR compliance, and
                        user consent logging.
                      </p>
                    </div>
                  </TextReveal>
                </ParallaxText>
                <ParallaxText offset={10}>
                  <TextReveal delay={0.5}>
                    <div className="flex gap-3">
                      <span className="text-[#fd5b38] shrink-0">•</span>
                      <p className="text-[#d1d5dc]">
                        Algorithmic Bias: Continuous retraining with diverse
                        datasets + bias testing scripts.
                      </p>
                    </div>
                  </TextReveal>
                </ParallaxText>
                <ParallaxText offset={8}>
                  <TextReveal delay={0.6}>
                    <div className="flex gap-3">
                      <span className="text-[#fd5b38] shrink-0">•</span>
                      <p className="text-[#d1d5dc]">
                        Over-Reliance on AI: Explainable AI layer keeps humans
                        in the loop.
                      </p>
                    </div>
                  </TextReveal>
                </ParallaxText>
                <ParallaxText offset={6}>
                  <TextReveal delay={0.7}>
                    <div className="flex gap-3">
                      <span className="text-[#fd5b38] shrink-0">•</span>
                      <p className="text-[#d1d5dc]">
                        Accessibility Gaps: Regular audits using WCAG tooling
                        and screen-reader validation.
                      </p>
                    </div>
                  </TextReveal>
                </ParallaxText>
              </div>
              <ParallaxText offset={10}>
                <TextReveal delay={0.9}>
                  <p className="text-[#d1d5dc]">
                    The system follows the IEEE Ethical AI framework—ensuring
                    accountability, transparency, and human oversight.
                  </p>
                </TextReveal>
              </ParallaxText>
            </div>
          </ContentCard>
        </div>
      </Section>

      {/* Section 17: Conclusion*/}
      <Section id="problem" className="relative">
        <div className="absolute top-0 right-0 w-64 h-64 bg-[#fd5b38] rounded-full blur-[150px] opacity-10 pointer-events-none" />
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <ContentCard>
            <div className="flex justify-around">
              <SectionHeader number="17" title="Conclusion" />
            </div>
            <div className="mt-6 md:mt-8 space-y-4">
              <ParallaxText offset={20}>
                <TextReveal delay={0.2}>
                  <p className="text-[#d1d5dc] text-center">
                    The AI-Powered UX Optimization Tool proves that AI and
                    human-centered design can coexist synergistically.
                  </p>
                </TextReveal>
              </ParallaxText>
              <ParallaxText offset={20}>
                <TextReveal delay={0.2}>
                  <p className="text-[#d1d5dc] text-center">
                    It transforms UX evaluation from a manual, reactive task
                    into a predictive, continuous learning process.
                  </p>
                </TextReveal>
              </ParallaxText>
              <ParallaxText offset={20}>
                <TextReveal delay={0.2}>
                  <p className="text-[#d1d5dc] text-center">
                    By embedding automation within the designer's workflow, the
                    tool frees creative energy for problem solving and
                    innovation, fulfilling the core goal of HCAI: empowering
                    humans rather than replacing them.
                  </p>
                </TextReveal>
              </ParallaxText>
            </div>
          </ContentCard>
        </div>
      </Section>

      {/* Section 18: Future Scope */}
      <Section id="problem" className="relative">
        <div className="absolute top-0 right-0 w-64 h-64 bg-[#fd5b38] rounded-full blur-[150px] opacity-10 pointer-events-none" />
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <ContentCard>
            <SectionHeader number="18" title="Future Scope" />
            <div className="mt-6 md:mt-8 space-y-4">
              <div className="space-y-3 ml-0 md:ml-4">
                <ParallaxText offset={12}>
                  <TextReveal delay={0.4}>
                    <div className="flex gap-3">
                      <span className="text-[#fd5b38] shrink-0">•</span>
                      <p className="text-[#d1d5dc]">
                        Figma Plugin Integration – Inline AI feedback during
                        design iterations.
                      </p>
                    </div>
                  </TextReveal>
                </ParallaxText>
                <ParallaxText offset={10}>
                  <TextReveal delay={0.5}>
                    <div className="flex gap-3">
                      <span className="text-[#fd5b38] shrink-0">•</span>
                      <p className="text-[#d1d5dc]">
                        Team Collaboration Mode – Multi-user real-time insight
                        reviews.
                      </p>
                    </div>
                  </TextReveal>
                </ParallaxText>
                <ParallaxText offset={8}>
                  <TextReveal delay={0.6}>
                    <div className="flex gap-3">
                      <span className="text-[#fd5b38] shrink-0">•</span>
                      <p className="text-[#d1d5dc]">
                        Predictive UX Scoring – Forecast usability before
                        testing.
                      </p>
                    </div>
                  </TextReveal>
                </ParallaxText>
                <ParallaxText offset={6}>
                  <TextReveal delay={0.7}>
                    <div className="flex gap-3">
                      <span className="text-[#fd5b38] shrink-0">•</span>
                      <p className="text-[#d1d5dc]">
                        Cross-Language Support – Global accessibility.
                      </p>
                    </div>
                  </TextReveal>
                </ParallaxText>
                <ParallaxText offset={6}>
                  <TextReveal delay={0.7}>
                    <div className="flex gap-3">
                      <span className="text-[#fd5b38] shrink-0">•</span>
                      <p className="text-[#d1d5dc]">
                        Continuous Accessibility API Linkage – Live WCAG
                        compliance monitoring.
                      </p>
                    </div>
                  </TextReveal>
                </ParallaxText>
              </div>
              <ParallaxText offset={10}>
                <TextReveal delay={0.9}>
                  <p className="text-[#d1d5dc] mt-12">
                    Long-term, the platform aspires to be an autonomous UX
                    intelligence ecosystem-learning, context-aware, and
                    ethically aligned with human values.
                  </p>
                </TextReveal>
              </ParallaxText>
            </div>
          </ContentCard>
        </div>
      </Section>

      {/* Section 19: Key Takeaways */}
      <Section id="problem" className="relative">
        <div className="absolute top-0 right-0 w-64 h-64 bg-[#fd5b38] rounded-full blur-[150px] opacity-10 pointer-events-none" />
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <ContentCard>
            <SectionHeader number="19" title="Key Takeaways" />
            <div className="mt-6 md:mt-8 space-y-4">
              <div className="space-y-3 ml-0 md:ml-4">
                <ParallaxText offset={12}>
                  <TextReveal delay={0.4}>
                    <div className="flex gap-3">
                      <span className="text-[#fd5b38] shrink-0">•</span>
                      <p className="text-[#d1d5dc]">
                        Efficiency: Automates insight generation - 50 % faster
                        analysis.
                      </p>
                    </div>
                  </TextReveal>
                </ParallaxText>
                <ParallaxText offset={10}>
                  <TextReveal delay={0.5}>
                    <div className="flex gap-3">
                      <span className="text-[#fd5b38] shrink-0">•</span>
                      <p className="text-[#d1d5dc]">
                        Objectivity: AI-validated recommendations enhance design
                        credibility.
                      </p>
                    </div>
                  </TextReveal>
                </ParallaxText>
                <ParallaxText offset={8}>
                  <TextReveal delay={0.6}>
                    <div className="flex gap-3">
                      <span className="text-[#fd5b38] shrink-0">•</span>
                      <p className="text-[#d1d5dc]">
                        Predictive UX Scoring – Forecast usability before
                        testing.
                      </p>
                    </div>
                  </TextReveal>
                </ParallaxText>
                <ParallaxText offset={6}>
                  <TextReveal delay={0.7}>
                    <div className="flex gap-3">
                      <span className="text-[#fd5b38] shrink-0">•</span>
                      <p className="text-[#d1d5dc]">
                        Inclusivity: Integrated accessibility audits reduce
                        exclusion risks.
                      </p>
                    </div>
                  </TextReveal>
                </ParallaxText>
                <ParallaxText offset={6}>
                  <TextReveal delay={0.7}>
                    <div className="flex gap-3">
                      <span className="text-[#fd5b38] shrink-0">•</span>
                      <p className="text-[#d1d5dc]">
                        Ethics: Transparent and accountable AI strengthens
                        trust.
                      </p>
                    </div>
                  </TextReveal>
                </ParallaxText>
                <ParallaxText offset={6}>
                  <TextReveal delay={0.7}>
                    <div className="flex gap-3">
                      <span className="text-[#fd5b38] shrink-0">•</span>
                      <p className="text-[#d1d5dc]">
                        Innovation: Positions UX research as a continuous
                        intelligent feedback loop.
                      </p>
                    </div>
                  </TextReveal>
                </ParallaxText>
              </div>
            </div>
          </ContentCard>
        </div>
      </Section>

      {/* Footer */}
      <Section className="border-b border-[#1e2939] pt-0 ">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 text-center">
          <TextReveal>
            <p className="text-[#6a7282] mb-2">Nithin Chandra Namani</p>
          </TextReveal>
          <TextReveal delay={0.1}>
            <p className="text-[#6a7282]">The New Age of AI in Design</p>
          </TextReveal>
        </div>
      </Section>
    </div>
  );
}

export default AgeOfAIPage;
